import type { App } from "@open-booking/server";
import type { CreatePropertyPayload, PublicPriceCalculationPayload, UpdatePropertyPayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetPropertiesResponse = App["~Routes"]["properties"]["get"]["response"]["200"];
type GetPropertyResponse = App["~Routes"]["properties"][":id"]["get"]["response"]["200"];
type CreatePropertyResponse = App["~Routes"]["properties"]["post"]["response"]["200"];
type UpdatePropertyResponse = App["~Routes"]["properties"][":id"]["put"]["response"]["200"];
type DeletePropertyResponse = App["~Routes"]["properties"][":id"]["delete"]["response"]["200"];
type PreviewBookingPriceResponse = App["~Routes"]["properties"][":id"]["price-preview"]["post"]["response"]["200"];

export const propertyApi = createApi({
	reducerPath: "propertyApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["Property"],
	endpoints: (builder) => ({
		// biome-ignore lint/suspicious/noConfusingVoidType: RTK Query uses `| void` so the hook accepts no argument
		getProperties: builder.query<GetPropertiesResponse, { search?: string } | void>({
			query:
				(params): EdenCall<GetPropertiesResponse> =>
				(client) =>
					client.properties.get({ query: params ?? {} }),
			providesTags: ["Property"],
		}),
		getProperty: builder.query<GetPropertyResponse, string>({
			query:
				(id): EdenCall<GetPropertyResponse> =>
				(client) =>
					client.properties({ id }).get(),
			providesTags: ["Property"],
		}),
		createProperty: builder.mutation<CreatePropertyResponse, CreatePropertyPayload>({
			query:
				(payload): EdenCall<CreatePropertyResponse> =>
				(client) =>
					client.properties.post(payload),
			invalidatesTags: ["Property"],
		}),
		updateProperty: builder.mutation<UpdatePropertyResponse, { id: string; data: UpdatePropertyPayload }>({
			query:
				({ id, data }): EdenCall<UpdatePropertyResponse> =>
				(client) =>
					client.properties({ id }).put(data),
			invalidatesTags: ["Property"],
		}),
		deleteProperty: builder.mutation<DeletePropertyResponse, string>({
			query:
				(id): EdenCall<DeletePropertyResponse> =>
				(client) =>
					client.properties({ id }).delete(),
			invalidatesTags: ["Property"],
		}),
		previewBookingPrice: builder.query<PreviewBookingPriceResponse, { id: string; data: PublicPriceCalculationPayload }>({
			query:
				({ id, data }): EdenCall<PreviewBookingPriceResponse> =>
				(client) =>
					client.properties({ id })["price-preview"].post(data),
		}),
	}),
});

export const {
	useGetPropertiesQuery,
	useGetPropertyQuery,
	useCreatePropertyMutation,
	useUpdatePropertyMutation,
	useDeletePropertyMutation,
	usePreviewBookingPriceQuery,
} = propertyApi;
